import { useEffect, useState } from "react";
import {
  Card,
  Badge,
  Text,
  Image,
  Flex,
  Modal,
  Anchor,
  Group,
  Select,
  Stack,
  Box,
  Title,
  Divider,
  Paper,
  Spoiler,
} from "@mantine/core";
import { StarIcon } from "../../shared/svg/StarIcon";
import { useTranslation } from "react-i18next";
import useCoursesStore from "../../store/useCoursesStore";
import { IconExternalLink } from "../../shared/svg/IconExternalLink";
import {
  formatPlatform,
  getCurrencySymbol,
  getLanguageName,
} from "../../utils/helpers";
import { useMediaQuery } from "@mantine/hooks";

interface LearningCourse {
  course: { id: number };
  status: "saved" | "in_progress" | "completed";
}

interface CourseCardProps {
  id?: number;
  image: string;
  platform: string;
  title: string;
  description: string;
  rating: number | "Unrated";
  pdf?: boolean;
  isLearning?: boolean;
  url?: string;
  handleOnClick?: (id: number) => void;
  authors: {
    name: string;
  }[];
  category: string;
  topics: {
    topic_name: string;
  }[];
  languages: {
    language: string;
  }[];
  organization: string;
  price: number | null;
  currency: string | null;
  is_free: boolean;
  onCategoryClick?: (category: string, topic?: string) => void;
}

export const CourseCard: React.FC<CourseCardProps> = ({
  id,
  image,
  platform,
  title,
  authors,
  description,
  rating,
  pdf,
  handleOnClick,
  isLearning,
  url,
  category,
  topics,
  languages,
  organization,
  price,
  currency = "USD",
  is_free,
  onCategoryClick,
}) => {
  const [opened, setOpened] = useState(false);
  const { t } = useTranslation();
  const matches = useMediaQuery("(max-width: 650px)");

  const { learningCourses, changeLearningCourseStatus, currentTab } =
    useCoursesStore();

  const [localStatus, setLocalStatus] = useState<
    LearningCourse["status"] | null
  >(null);

  useEffect(() => {
    if (opened) {
      const found = learningCourses.results.find(
        (learningCourse) => learningCourse.course.id === id
      );
      if (found?.status) {
        setLocalStatus(found.status as LearningCourse["status"]);
      }
    }
  }, [opened, id, learningCourses]);

  const getCourseStatus = (): LearningCourse["status"] | null => {
    const found = learningCourses.results.find(
      (learningCourse) => learningCourse.course.id === id
    );
    if (!found?.status) return null;

    if (["in_progress", "completed", "saved"].includes(found.status)) {
      return found.status as LearningCourse["status"];
    }

    return null;
  };

  const getCourseId = () => {
    const found = learningCourses.results.find(
      (learningCourse) => learningCourse.course.id === id
    );
    return found?.id ?? null;
  };

  const renderStatusBadge = (status: LearningCourse["status"]) => {
    const statusColors = {
      saved: "gray",
      in_progress: "blue",
      completed: "green",
    };

    const statusLabels = {
      saved: t("saved") || "Saved",
      in_progress: t("in_progress") || "In Progress",
      completed: t("completed") || "Completed",
    };

    return (
      <Badge color={statusColors[status]} size="lg">
        {statusLabels[status]}
      </Badge>
    );
  };

  const currentStatus = getCourseStatus();
  const courseId = getCourseId();

  return (
    <>
      <Card
        padding={pdf ? "32px" : "16px"}
        radius={pdf ? "48px" : "24px"}
        withBorder
        pos="relative"
        h="100%"
        style={{ cursor: "pointer" }}
        className="course-card"
        onClick={() => setOpened(true)}
      >
        <Card.Section>
          <Badge
            color="#FFFFFF"
            pos="absolute"
            p={pdf ? 36 : 18}
            right={pdf ? 32 : 16}
            top={pdf ? 32 : 16}
          >
            <Text c="#000" tt="none" size={pdf ? "32px" : "16px"}>
              {formatPlatform(platform)}
            </Text>
          </Badge>
          <Image src={image} height={pdf ? 278 : 180} alt="Course" />
        </Card.Section>
        <Flex mt={16} h="100%" direction="column" justify="space-between">
          <div>
            <Text
              fw="bold"
              mt={16}
              size={pdf ? "28px" : "20px"}
              lh={pdf ? "38px" : undefined}
            >
              {title}
            </Text>
            <Text mt={16} size={pdf ? "28px" : "16px"}>
              {organization}
            </Text>
          </div>
          <Flex
            gap={8}
            mt={pdf ? 32 : 16}
            align="center"
            justify="space-between"
          >
            <Flex gap={8} align="center">
              <StarIcon />
              <Text size={pdf ? "28px" : "16px"}>
                {rating === "Unrated" ? t("unrated") : rating}
              </Text>
            </Flex>
            {!pdf && (
              <Text
                onClick={(e) => {
                  e.stopPropagation();
                  if (handleOnClick && id) handleOnClick(id);
                }}
                size={pdf ? "32px" : "24px"}
                style={{
                  cursor: "pointer",
                }}
              >
                {isLearning ? "💜" : "🤍"}{" "}
              </Text>
            )}
          </Flex>
        </Flex>

        {!pdf && currentStatus && (
          <Flex
            pos="absolute"
            top={pdf ? 32 : 16}
            left={pdf ? 32 : 16}
            align="center"
            justify="center"
          >
            {renderStatusBadge(currentStatus)}
          </Flex>
        )}
      </Card>
      <Modal
        zIndex={302}
        title={
          <Flex justify="space-between" align="start">
            <Box style={{ flex: 1 }}>
              <Title order={matches ? 4 : 3}>{title}</Title>
              {organization && (
                <Text c="dimmed" size="sm">
                  ({organization})
                </Text>
              )}
            </Box>

            {url && (
              <Anchor
                href={url}
                target="_blank"
                color="blue"
                fw={500}
                style={{
                  flexShrink: 0,
                  marginLeft: "1rem",
                  whiteSpace: "nowrap",
                }}
              >
                <IconExternalLink />
              </Anchor>
            )}
          </Flex>
        }
        opened={opened}
        onClose={() => setOpened(false)}
        size="lg"
        padding={30}
        withCloseButton
      >
        <Stack>
          {/* Зображення */}
          {image && (
            <Box
              style={{
                height: 200,
                overflow: "hidden",
                borderRadius: "8px",
              }}
            >
              <Image
                src={image}
                alt="Course"
                fit="cover"
                height="100%"
                width="100%"
                style={{ objectFit: "cover" }}
              />
            </Box>
          )}

          {/* Категорії */}
          {category && (
            <Group gap="xs">
              <Badge
                color="blue"
                size="lg"
                variant="light"
                className={onCategoryClick ? "clickable-badge" : undefined}
                onClick={() => {
                  if (onCategoryClick) {
                    onCategoryClick(category);
                    setOpened(false);
                  }
                }}
              >
                {category}
              </Badge>
            </Group>
          )}

          {/* Опис */}
          {description && (
            <Spoiler
              maxHeight={86}
              showLabel={
                <Text
                  style={{
                    color: "#6c6c6c",
                    fontSize: "14px",
                    fontWeight: "bold",
                    right: 0,
                  }}
                >
                  {t("read_more")}
                </Text>
              }
              hideLabel={
                <Text
                  style={{
                    color: "#6c6c6c",
                    fontSize: "14px",
                    fontWeight: "bold",
                    right: 0,
                  }}
                >
                  {t("read_less")}
                </Text>
              }
              transitionDuration={300}
            >
              <Text size="sm">{description}</Text>
            </Spoiler>
          )}

          {/* Теми */}
          {topics?.length > 0 && (
            <Group gap="xs" wrap="wrap">
              {topics.map((topic, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  color="grape"
                  className="clickable-topic"
                  onClick={() => {
                    if (onCategoryClick) {
                      onCategoryClick(category, topic.topic_name);
                      setOpened(false);
                    }
                  }}
                >
                  {topic.topic_name}
                </Badge>
              ))}
            </Group>
          )}

          {/* Новий стилізований блок */}
          {(languages?.length > 0 ||
            authors?.length > 0 ||
            rating !== null ||
            price !== null ||
            is_free ||
            platform) && (
            <Paper
              shadow="xs"
              radius="md"
              p="md"
              withBorder
              mt="md"
              style={{
                backgroundColor: "#f9f9fc",
              }}
            >
              <Stack gap="sm">
                {platform && (
                  <Box>
                    <Text fw={600} size="sm" mb={4}>
                      {t("platform")}:
                    </Text>
                    <Paper
                      shadow="xs"
                      radius="md"
                      withBorder
                      style={{
                        backgroundColor: "#f4f7ff", // більш насичений фон
                        color: "#004d99", // акцентний синій
                        fontWeight: 600,
                        fontSize: "18px",
                        display: "inline-block",
                        padding: "4px 16px",
                        textTransform: "capitalize",
                      }}
                    >
                      {formatPlatform(platform)}
                    </Paper>
                  </Box>
                )}

                {languages?.length > 0 && (
                  <Box>
                    <Text fw={600} size="sm" mb={4}>
                      {t("languages")}:
                    </Text>
                    <Text size="sm" c="dimmed">
                      {languages
                        .map((l) => getLanguageName(l.language))
                        .filter((name) => name.trim() !== "")
                        .join(", ")}
                    </Text>
                  </Box>
                )}

                {/* Інструктори */}
                {authors?.length > 0 && (
                  <Box>
                    <Text fw={600} size="sm" mb={4}>
                      {t("instructor")}:
                    </Text>
                    <Text size="sm" c="dimmed">
                      {authors.map((a) => a.name).join(", ")}
                    </Text>
                  </Box>
                )}

                {/* Рейтинг + Ціна */}
                {(rating !== null || price !== null || is_free) && (
                  <Flex
                    justify="space-between"
                    align="center"
                    wrap="wrap"
                    gap="sm"
                    pt="sm"
                    mt="sm"
                  >
                    {rating !== null && (
                      <Text size="sm" c="dimmed">
                        <Text span fw={500} c="black">
                          {t("rating")}:
                        </Text>{" "}
                        {rating === "Unrated" ? t("unrated") : rating}
                      </Text>
                    )}

                    {(price !== null || (currency && price !== null)) && (
                      <Text size="sm" c="dimmed">
                        <Text span fw={500} c="black">
                          {t("price")}:
                        </Text>{" "}
                        {price} {currency ? getCurrencySymbol(currency) : "$"}
                      </Text>
                    )}

                    {price === null && is_free && (
                      <Text size="sm" c="dimmed">
                        <Text span fw={500} c="black">
                          {t("price")}:
                        </Text>{" "}
                        {t("free")}
                      </Text>
                    )}

                    {price === null && !is_free && (
                      <Text size="sm" color="dimmed">
                        <strong>{t("price")}:</strong> {t("no_data")}
                      </Text>
                    )}
                  </Flex>
                )}
              </Stack>
            </Paper>
          )}

          <Divider my="md" color="gray.4" />

          <Flex
            justify="space-between"
            align="center"
            wrap="wrap"
            gap={10}
            direction={matches ? "column" : "row"}
          >
            {currentStatus ? (
              <Select
                comboboxProps={{
                  zIndex: 302,
                }}
                data={[
                  { value: "saved", label: t("saved") },
                  { value: "in_progress", label: t("in_progress") },
                  { value: "completed", label: t("completed") },
                ]}
                value={localStatus}
                onChange={(newStatus) => {
                  if (newStatus) {
                    setLocalStatus(newStatus as LearningCourse["status"]);
                    if (courseId && localStatus) {
                      changeLearningCourseStatus(courseId, newStatus);
                    }
                  }
                }}
                styles={{ input: { height: 42 } }}
              />
            ) : (
              <div />
            )}

            <Flex
              align="center"
              gap={10}
              onClick={(e) => {
                e.stopPropagation();
                if (handleOnClick && id) handleOnClick(id);
                if (currentTab === "My learning") setOpened(false);
              }}
              style={{ cursor: "pointer" }}
            >
              <Text size="sm" style={{ color: isLearning ? "#6f40e2" : "" }}>
                {isLearning ? t("in_learnings") : t("add_to_learnings")}
              </Text>

              <Text size="30px">{isLearning ? "💜" : "🤍"}</Text>
            </Flex>
          </Flex>
        </Stack>
      </Modal>
    </>
  );
};
